import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/resources`)
      .then((r) => setResources(r.data.resources || []))
      .catch(() => setResources([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="resources-page" className="bg-white">
      <section className="py-20 lg:py-28 bg-gradient-hero" data-testid="resources-hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">Resources &amp; guides</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy leading-[1.05]">
            Tools for the <span className="hl-teal">hard days</span> — and every day after.
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-muted-ink leading-relaxed max-w-2xl mx-auto">
            Practical, evidence-informed guides from our care team. Use them solo, bring them to your next session,
            or chat with our AI companion about them.
          </p>
        </div>
      </section>

      <section className="pb-20 lg:pb-28" data-testid="resources-grid-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <p className="text-center text-muted-ink" data-testid="resources-loading">Loading...</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {resources.map((r) => (
                <Link
                  key={r.id}
                  to={`/resources/${r.id}`}
                  className="gh-card flex flex-col group"
                  data-testid={`resource-card-${r.id}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="eyebrow">{r.category}</span>
                  </div>
                  <h3 className="font-display text-xl mb-3 text-navy group-hover:text-teal transition-colors leading-tight">
                    {r.title}
                  </h3>
                  <p className="text-muted-ink leading-relaxed mb-6 flex-1 text-[15px]">{r.summary}</p>
                  <div className="flex items-center justify-between text-sm pt-4 border-t border-slate-200">
                    <span className="flex items-center gap-1.5 text-muted-ink"><Clock className="w-3.5 h-3.5" /> {r.read_time}</span>
                    <span className="flex items-center gap-1 text-teal font-bold">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-cream" data-testid="resources-cta-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <BookOpen className="w-10 h-10 text-teal mx-auto mb-5" />
          <h2 className="font-display text-3xl sm:text-4xl text-navy mb-4">
            Need something that isn't here?
          </h2>
          <p className="text-lg text-muted-ink mb-8">
            Our care team creates new guides every month based on what our community asks for.
          </p>
          <a href="/contact" className="btn-outline-teal" data-testid="resources-request-link">
            Request a topic
          </a>
        </div>
      </section>
    </div>
  );
}
